import { Modal, ModalStatus, OverlayBackdrop } from "@ds/ds-snam/react";
import { Button } from "@ds/ds-snam/react";
import { ReactElement, useState, useRef } from "react";
import { CalendarViewType, DsLayout, EntityType, ResponseCode, ResponseStatus, UICommand } from "shared/constants";
import { useTranslation } from "react-i18next";
import { ICalendarDialogForm, ICalendarDialogFormData, ICalendarEvent, ICloneReservation } from "dataModels/b2b";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { hideNotification, showNotification } from "state/reducer/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import RangeCalendarForm from "./components/rangeCalendarForm";
import NotAvailableSlotsForm from "./components/notAvailableSlotsForm";
import SlotBookingForm from "./components/slotBookingForm";
import { bookSlot,  nonWorkingDaysCreate, nonWorkingDaysDelete, putCalendarTimeRange,  putUnvailableBayes } from "net/b2b";
import { INotificationAction, NotificationsStatusType } from "dataModels/stateModels/notification";
import DeleteMultipleSlots from "./components/deleteMultipleSlotsForm";
import { RootState } from "state/appStore";


interface IDialogCalendarProps {
    selectedEvent: ICalendarEvent | undefined,
    command: UICommand,
    loadEvents: () => void,
    onClose: (command?: UICommand) => void,
    externalLogic? : () => void;
    isDraft?:CalendarViewType;
    cloneData?:ICloneReservation|undefined;
}
const DialogCalendar: React.FC<IDialogCalendarProps> = (props): JSX.Element => {

    const { command, selectedEvent } = props;
    const { t } = useTranslation(["b2b", "app"]);
    const formRef = useRef<ICalendarDialogForm>(null);
    const dispatch = useDispatch();
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })


    const getContentForm = () => {
        let returnComponent: JSX.Element | undefined = undefined;
        switch (command) {
            case UICommand.CALENDAR_PUBBLICATION: //319
                returnComponent = <RangeCalendarForm ref={formRef} command={UICommand.CALENDAR_PUBBLICATION} />
                break;
            case UICommand.CALENDAR_CONFIGURE: //155
                returnComponent = <RangeCalendarForm ref={formRef} command={UICommand.CALENDAR_CONFIGURE} />
                break;
            case UICommand.CALENDAR_DELETE_SLOTS:
                returnComponent = <DeleteMultipleSlots ref={formRef} command={UICommand.CALENDAR_CONFIGURE}/>
                break;
            case UICommand.CALENDAR_SLOT_NOT_AVAILABLE: //271 e 295
                returnComponent = <NotAvailableSlotsForm ref={formRef} />
                break;
            case UICommand.CALENDAR_DELETE_PUBBLICATION: //315
                returnComponent = <RangeCalendarForm ref={formRef} command={UICommand.CALENDAR_DELETE_PUBBLICATION} />
                break;
            case UICommand.CALENDAR_BOOK_SLOT:
                returnComponent = <SlotBookingForm ref={formRef} command={UICommand.CALENDAR_BOOK_SLOT} selectedEvent={selectedEvent} isCloning={false}/>
                break;
            case UICommand.DELETE:
                returnComponent = <div>
                    <div slot="title" style={{color:layoutMode===DsLayout.DARK_MODE?"white":"black"}}>
                        <h2 className="ff-ubuntu ff-title-s ff-bold" style={{ margin: "0 auto",width:'fit-content',textAlign:'center' }} id="overlay-title">
                            {t('checkList.titles.4')}{/*Attenzione*/}
                        </h2>
                        <p style={{ margin: "0 auto",textAlign:'center',marginTop:'20px' }}>{t('checkList.sub_titles.19')}{/*Sei sicuro di voler salvare la checklist?*/}</p>
                    </div>
                </div>
                break;
            case UICommand.CALENDAR_NON_WORKING_DAYS_INSERT:
                returnComponent = <RangeCalendarForm ref={formRef} command={UICommand.CALENDAR_NON_WORKING_DAYS_INSERT} />
                break;
            case UICommand.CALENDAR_NON_WORKING_DAYS_DELETE:
                returnComponent = <RangeCalendarForm ref={formRef} command={UICommand.CALENDAR_NON_WORKING_DAYS_DELETE} />
                break;
            case UICommand.CALENDAR_MOVE_RESERVATION:
                returnComponent = <SlotBookingForm ref={formRef} command={UICommand.CALENDAR_BOOK_SLOT} selectedEvent={selectedEvent} isCloning={true}/>
                break;
            case UICommand.REVERT_RESERVATIONS: //155
                returnComponent = <RangeCalendarForm ref={formRef} command={UICommand.REVERT_RESERVATIONS} />
                break;
            default:
                break;
        }
        return returnComponent;
    }

    const getRequest = () => {
        let returnRequest = null;
        switch (command) {
            case UICommand.CALENDAR_PUBBLICATION: //319
            case UICommand.CALENDAR_CONFIGURE: //155
            case UICommand.CALENDAR_DELETE_PUBBLICATION: //315
            case UICommand.CALENDAR_DELETE_SLOTS: //298
            case UICommand.REVERT_RESERVATIONS:
                returnRequest = putCalendarTimeRange;
                break;
            case UICommand.CALENDAR_SLOT_NOT_AVAILABLE: // 271 e 295 
                returnRequest = putUnvailableBayes;
                break;
            case UICommand.CALENDAR_BOOK_SLOT:
                returnRequest = bookSlot;
                break;
            case UICommand.CALENDAR_NON_WORKING_DAYS_INSERT:
                returnRequest = nonWorkingDaysCreate;
                break;
            case UICommand.CALENDAR_NON_WORKING_DAYS_DELETE:
                returnRequest = nonWorkingDaysDelete;
                break;
            default:
                break;
        }
        return returnRequest;
    }

    const getPopupInfo = (type: string) => {
        let returnValue: string = '';
        switch (command) {
            case UICommand.CALENDAR_PUBBLICATION:
                returnValue = (type === 'title') ?
                    t('calendar.popups.snacks.0')/*"Calendar published successfully"*/
                    :
                    t('calendar.popups.snacks.1')/*"Gli slot pubblicati saranno visibili dai clienti"*/
                break;
            case UICommand.CALENDAR_CONFIGURE:
                returnValue = (type === 'title') ?
                    t('calendar.popups.snacks.2')/*"Calendario configurato correttamente",*/
                    :
                    t('calendar.popups.snacks.3')/*"I parametri impostati per la configurazione sono stati salvati correttamente",*/
                break;
            case UICommand.CALENDAR_DELETE_SLOTS:
                returnValue = (type === 'title') ?
                    t('calendar.popups.snacks.4') /*"Calendario aggiornato correttamente",*/
                    :
                    t('calendar.popups.snacks.5') //"Gli slot sono stati eliminati correttamente"
                break;
            case UICommand.CALENDAR_SLOT_NOT_AVAILABLE:
                returnValue = (type === 'title') ?
                    t('calendar.popups.snacks.6')/*"Indisponibilità inserita correttamente",*/
                    :
                    t('calendar.popups.snacks.7')/*"Lo slot e le baie sono state inserite come indisponibili in piattaforma"*/
                break;
            case UICommand.CALENDAR_DELETE_PUBBLICATION:
                returnValue = (type === 'title') ?
                    t('calendar.popups.snacks.4')/*"Calendario aggiornato correttamente",*/
                    :
                    t('calendar.popups.snacks.5')/*"Gli slot sono stati eliminati correttamente"*/
                break;
            case UICommand.CALENDAR_BOOK_SLOT:
                returnValue = (type === 'title') ?
                    t('calendar.popups.snacks.8')/**Slot prenotato correttamente */
                    :
                    t('calendar.popups.snacks.9')/**Lo slot è in stato in attesa di conferma */
                break;
            case UICommand.CALENDAR_NON_WORKING_DAYS_INSERT:
                returnValue = (type === 'title') ?
                        t('calendar.popups.snacks.10')
                        :
                        t('calendar.popups.snacks.11')
                    break;
            case UICommand.CALENDAR_NON_WORKING_DAYS_DELETE:
                returnValue = (type === 'title') ?
                        t('calendar.popups.snacks.12')
                        :
                        t('calendar.popups.snacks.13')
                    break;
            case UICommand.REVERT_RESERVATIONS:
                returnValue = (type === 'title') ?
                        t('calendar.popups.snacks.14')
                        :
                        t('calendar.popups.snacks.15')
                    break;
            default:
                break;
        }
        return returnValue;
    }

    const saveFormData = (formValues: ICalendarDialogFormData) => {
        const request = getRequest();
        const popupTitle: string = getPopupInfo('title');
        const popupText: string = getPopupInfo('text');
        dispatch(requestPending());
        //console.log(formValues)
        request!(formValues,props.isDraft===CalendarViewType.DRAFT?CalendarViewType.DRAFT:CalendarViewType.PUBLIC)
            .then((response) => {
                let popUpAction: INotificationAction;
                const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? 'Unprocessable content': popupTitle );
                const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:(command===UICommand.REVERT_RESERVATIONS?response.data.message:popupText);
                const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                popUpAction = {
                    show: true,
                    title: popUpTitle,
                    description: popUpDescription,
                    status: popUpStatus,
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
                props.onClose();
                props.loadEvents();
                
                /*
                if (command === UICommand.CALENDAR_BOOK_SLOT && response.data['status'] === "KO" && response.data['code'] === "10") {
                    props.onClose(UICommand.CALENDAR_BOOK_SLOT_ERROR);
                } else {
                    let popUpAction: INotificationAction;
                    popUpAction = {
                        show: true,
                        title: popupTitle,
                        description: popupText,
                        status: "confirm",
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    dispatch(showNotification(popUpAction));
                    props.onClose();
                    props.loadEvents();
                }*/

            })
            .catch(error => {
                console.log(error);
                let popUpAction: INotificationAction;
                    popUpAction = {
                        show: true,
                        title: "Error",
                        description: error.response.data,
                        status: "error",
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    dispatch(showNotification(popUpAction));
                    props.onClose();
                    props.loadEvents();
            })
            .finally(() => {
                dispatch(requestPristine());
            })
    }

    const getSubmitButtonLabel = () => {
        let returnLabel: string = '';
        switch (command) {
            case UICommand.CALENDAR_PUBBLICATION:
                returnLabel = t('calendar.popups.buttons.5')//'Pubblica'
                break;
            case UICommand.CALENDAR_CONFIGURE:
            case UICommand.CALENDAR_DELETE_SLOTS:
            case UICommand.CALENDAR_SLOT_NOT_AVAILABLE:
            case UICommand.CALENDAR_DELETE_PUBBLICATION:
            case UICommand.CALENDAR_BOOK_SLOT:
            case UICommand.DELETE:
            case UICommand.CALENDAR_NON_WORKING_DAYS_INSERT:
            case UICommand.CALENDAR_NON_WORKING_DAYS_DELETE:
            case UICommand.CALENDAR_MOVE_RESERVATION:
            case UICommand.REVERT_RESERVATIONS:
                returnLabel = t('calendar.popups.buttons.8')//'Conferma'
                break;
            default:
                break;
        }
        return returnLabel;
    }

    return (
        <>
            <div style={{ zIndex: 1200, position: "relative", fontFamily: 'Lato, Lato-local, sans-serif' }}>
                <OverlayBackdrop />

                {command && command === UICommand.CALENDAR_BOOK_SLOT_ERROR ?
                    <ModalStatus
                        notificationStatus="error"
                        popupSize="medium"
                        popupTitle={t('calendar.popups.slotBooking.12')}//"Superamento dei limiti contrattuali"
                        popupDescription={t('calendar.popups.slotBooking.11')}//"Non e' possibile pianificare questo slot a causa del superamento dei limiti contrattuali"
                        primaryButtonLabel={t('calendar.popups.buttons.9')}//"Torna al calendario"
                        zIndex={999}
                        verticalPosition="middle"
                        /*onClosePopup={()=>{
                            props.onClose()
                        }}*/
                        onConfirmPopup={() => {
                            props.onClose();
                        }}
                    />
                :
                    <Modal
                        modalSize="medium"
                        iconExtra={false}
                        overlayIcon="icon-open-mail"
                        overlayDescriptionId="overlay-description"
                        is-gradient-color={true}
                        onCloseModal={() =>
                            props.onClose()
                        }
                    >

                        <div slot="title" style={{ background: '', height: '100px' }}>
                            <div style={{ width: 'fit-content', margin: '0 auto', color: '#db6100' }}>
                                <ds-icon
                                    id="arrowTopBar"
                                    icon={"icon-info-circle"}
                                    icon-size="xlarge"
                                    icon-filled="false"
                                ></ds-icon>
                            </div>
                        </div>

                        <div slot="text">
                            {getContentForm()}
                        </div>

                        <div
                            slot="footer"
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "8px",
                                alignItems: "center",
                                marginTop: '20px'
                            }}
                        >
                            <div style={{ margin: '0 auto', width: 'fit-content', }}>
                                <div style={{ margin: '0 auto', width: 'fit-content', float: 'left' }}>
                                    <Button
                                        buttonType="button"
                                        buttonUIVariant="secondary"
                                        buttonSize="medium"
                                        buttonLabel={t('calendar.popups.buttons.6')}//'Annulla'
                                        onClickEvent={() => {
                                            props.onClose()
                                        }}
                                    ></Button>
                                </div>
                                <div style={{ margin: '0 auto', width: 'fit-content', float: 'right', marginLeft: '10px' }}>
                                    <Button
                                        buttonType="button"
                                        buttonUIVariant="primary"
                                        buttonSize="medium"
                                        buttonLabel={getSubmitButtonLabel()}
                                        onClickEvent={() => {
                                            if(props.externalLogic){
                                                props.externalLogic();
                                            }else{
                                                if (formRef.current) {
                                                    const formValues = formRef.current.getFormValues();
                                                    //console.log(formValues)
                                                    saveFormData(formValues);                                                 
                                                }
                                            }
                                        }}
                                    ></Button>
                                </div>
                            </div>
                        </div>

                    </Modal>
                }

            </div>
        </>
    );
}

export default DialogCalendar;